import { Component, computed, inject, input } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { HlmDropdownMenuImports } from '@mb/ui/dropdown-menu';
import { type NgDiagramNodeTemplate, type SimpleNode } from 'ng-diagram';
import type { OrganizationUnitType } from '../../../shared';
import type { OrgUnitNodeData } from './org-chart.model';
import { OrgChartStore } from './org-chart.state';
import { OePositionRowComponent } from './oe-position-row.component';
import { OeStationRowComponent } from './oe-station-row.component';

const TYPE_LABEL_KEYS: Record<OrganizationUnitType, string> = {
  organization: 'organigram.oeType.organization',
  company: 'organigram.oeType.company',
  profit_center: 'organigram.oeType.profitCenter',
  department: 'organigram.oeType.department',
};

const CARD_BASE =
  'group relative flex h-full w-full cursor-move flex-col rounded-xl border shadow-sm transition-[border-color,box-shadow] duration-150 hover:border-primary/50 hover:shadow-md';

/**
 * Card colour per OE type so the hierarchy levels are visually distinct.
 *
 * Down the tree: primary for the root, indigo for a company, emerald for a
 * profit centre, amber for a department. (Slate was a station's, which is a
 * row inside its OE's card since it stopped being an OE.)
 *
 * Emerald and not sky, which was the first choice and was wrong — seen in a
 * browser, `sky-50` beside `indigo-50` is two shades of almost-white, and
 * the profit centre sits directly under the company it has to be told apart
 * from. Emerald is the nearest hue that is distinct from both its neighbours.
 */
const TYPE_CLASSES: Record<OrganizationUnitType, string> = {
  organization: 'border-primary-300 bg-primary text-primary-foreground',
  company: 'border-indigo-300 bg-indigo-50 text-indigo-900',
  profit_center: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  department: 'border-amber-300 bg-amber-50 text-amber-900',
};

const SUBTITLE_CLASSES: Record<OrganizationUnitType, string> = {
  organization: 'truncate text-[11px] text-primary-foreground',
  company: 'truncate text-[11px] text-indigo-400',
  profit_center: 'truncate text-[11px] text-emerald-500',
  department: 'truncate text-[11px] text-amber-500',
};

/**
 * The "n Stellen" and "n Stationen" sections under the header, in the card's
 * own hue: the divider, the toggle's text and count pill, and the accent bar
 * of each position row. The root's card is filled primary, so its sections
 * turn light-on-dark. Station rows keep their own slate.
 */
const SECTION_CLASSES: Record<
  OrganizationUnitType,
  { toggle: string; pill: string; accent: string }
> = {
  organization: {
    toggle:
      'border-primary-300 text-primary-foreground/80 hover:text-primary-foreground',
    pill: 'bg-white/20 text-primary-foreground',
    accent: 'border-l-primary-300',
  },
  company: {
    toggle: 'border-indigo-200 text-indigo-500 hover:text-indigo-800',
    pill: 'bg-indigo-100 text-indigo-700',
    accent: 'border-l-indigo-400',
  },
  profit_center: {
    toggle: 'border-emerald-200 text-emerald-600 hover:text-emerald-800',
    pill: 'bg-emerald-100 text-emerald-700',
    accent: 'border-l-emerald-400',
  },
  department: {
    toggle: 'border-amber-200 text-amber-600 hover:text-amber-800',
    pill: 'bg-amber-100 text-amber-700',
    accent: 'border-l-amber-400',
  },
};

/**
 * Organization-unit (OE) card: the header, and under it the unit's Stellen
 * and its stations.
 *
 * Both are rows inside the card, not nodes of their own, each behind a toggle
 * ("n Stellen", "n Stationen") that starts closed. A plain `@if` rather than a second
 * accordion: the rows hold accordions of their own, and an outer one measures
 * its height once on opening — a row opening after it would be clipped.
 */
@Component({
  selector: 'org-unit-node',
  standalone: true,
  imports: [
    HlmDropdownMenuImports,
    TranslocoPipe,
    OePositionRowComponent,
    OeStationRowComponent,
  ],
  template: `
    <div [class]="cardClasses()" data-no-pan="true">
      <!-- 62px plus the card's two 1px borders: OE_HEIGHT. -->
      <div
        class="flex h-[62px] shrink-0 items-center justify-between gap-2 px-3"
      >
        <div class="min-w-0">
          <p class="truncate text-sm font-semibold">{{ data().name }}</p>
          <p [class]="subtitleClasses()">
            {{ typeLabelKey() | transloco }}
          </p>
        </div>
        <div
          class="flex shrink-0 items-center gap-1"
          data-testid="oe-card-actions"
        >
          @if (canConfigure()) {
            <button
              type="button"
              class="rounded-md p-1 hover:bg-black/10"
              [title]="'organigram.node.settings' | transloco"
              data-no-pan="true"
              data-no-drag="true"
              (click)="onSettings($event)"
            >
              <span class="material-icons text-base">settings</span>
            </button>
          }
          @if (data().isDeletionLocked) {
            <span
              class="material-icons text-base"
              [title]="'organigram.node.locked' | transloco"
              >lock</span
            >
          }
        </div>
      </div>

      @if (positionCount() > 0 || stationCount() > 0) {
        <div class="flex min-h-0 flex-col rounded-b-[11px] bg-white/60">
          @if (positionCount() > 0) {
            <button
              type="button"
              [class]="toggleClasses()"
              data-no-pan="true"
              data-no-drag="true"
              data-testid="oe-positions-toggle"
              [attr.aria-expanded]="positionsExpanded()"
              (click)="togglePositions($event)"
            >
              <span [class]="pillClasses()">{{ positionCount() }}</span>
              <span>
                {{
                  (positionCount() === 1
                    ? 'organigram.oe.positionOne'
                    : 'organigram.oe.positionOther'
                  ) | transloco
                }}
              </span>
              <span
                class="material-icons ms-auto text-base transition-transform duration-150"
                [class.rotate-180]="positionsExpanded()"
                aria-hidden="true"
                >expand_more</span
              >
            </button>
            @if (positionsExpanded()) {
              <div class="flex flex-col gap-1 px-2 pb-2">
                @for (position of data().positions; track position.positionId) {
                  <org-oe-position-row
                    [position]="position"
                    [accentClass]="section().accent"
                  />
                }
              </div>
            }
          }
          @if (stationCount() > 0) {
            <button
              type="button"
              [class]="toggleClasses()"
              data-no-pan="true"
              data-no-drag="true"
              data-testid="oe-stations-toggle"
              [attr.aria-expanded]="stationsExpanded()"
              (click)="toggleStations($event)"
            >
              <span [class]="pillClasses()">{{ stationCount() }}</span>
              <span>
                {{
                  (stationCount() === 1
                    ? 'organigram.oe.stationOne'
                    : 'organigram.oe.stationOther'
                  ) | transloco
                }}
              </span>
              <span
                class="material-icons ms-auto text-base transition-transform duration-150"
                [class.rotate-180]="stationsExpanded()"
                aria-hidden="true"
                >expand_more</span
              >
            </button>
            @if (stationsExpanded()) {
              <div class="flex flex-col gap-1 px-2 pb-2">
                @for (station of data().stations; track station.stationId) {
                  <org-oe-station-row [station]="station" />
                }
              </div>
            }
          }
        </div>
      }

      <!--
        The trigger goes when neither entry is left: on a root that already has
        its company, both are gone, and a menu that opens onto nothing reads as
        a fault.
      -->
      @if (canAddSomething()) {
        <button
          type="button"
          class="absolute -bottom-3 left-1/2 z-10 flex size-6 -translate-x-1/2 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 opacity-0 shadow-sm transition-opacity duration-150 group-hover:opacity-100 focus-visible:opacity-100 aria-expanded:opacity-100 hover:border-primary hover:bg-primary hover:text-primary-foreground"
          [title]="'organigram.node.add' | transloco"
          data-no-pan="true"
          data-no-drag="true"
          [hlmDropdownMenuTrigger]="addMenu"
          (click)="$event.stopPropagation()"
        >
          <span class="material-icons text-base" aria-hidden="true">add</span>
        </button>
      }
      @if (canDelete()) {
        <button
          type="button"
          class="absolute -right-2.5 -top-2.5 z-10 flex size-6 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 opacity-0 shadow-sm transition-opacity duration-150 group-hover:opacity-100 focus-visible:opacity-100 hover:border-destructive hover:bg-destructive/10 hover:text-destructive"
          [title]="'organigram.node.deleteOe' | transloco"
          data-no-pan="true"
          data-no-drag="true"
          (click)="onDelete($event)"
        >
          <span class="material-icons text-base" aria-hidden="true"
            >remove</span
          >
        </button>
      }
    </div>

    <ng-template #addMenu>
      <hlm-dropdown-menu>
        <!--
          Absent where the placement rule leaves no room: under a root that
          already has its company there is nothing to offer, and an entry that
          opens a form with an empty type list is worse than no entry.
        -->
        @if (data().canCreateChildOe) {
          <button hlmDropdownMenuItem (triggered)="add('orgUnit')">
            <span class="flex w-full items-center gap-2">
              <span class="material-icons text-lg" aria-hidden="true"
                >account_tree</span
              >
              {{ 'organigram.node.addOe' | transloco }}
            </span>
          </button>
        }
        <!-- Both absent on the root: a placeholder for the company beneath it
             employs nobody and is no place of work. -->
        @if (data().canCreatePosition) {
          <button hlmDropdownMenuItem (triggered)="add('position')">
            <span class="flex w-full items-center gap-2">
              <span class="material-icons text-lg" aria-hidden="true"
                >work</span
              >
              {{ 'organigram.node.addPosition' | transloco }}
            </span>
          </button>
        }
        @if (data().canCreateStation) {
          <button hlmDropdownMenuItem (triggered)="add('station')">
            <span class="flex w-full items-center gap-2">
              <span class="material-icons text-lg" aria-hidden="true"
                >place</span
              >
              {{ 'organigram.node.addStation' | transloco }}
            </span>
          </button>
        }
      </hlm-dropdown-menu>
    </ng-template>
  `,
})
export class OrgUnitNodeComponent implements NgDiagramNodeTemplate<OrgUnitNodeData> {
  readonly store = inject(OrgChartStore);
  readonly node = input.required<SimpleNode<OrgUnitNodeData>>();
  readonly data = computed(() => this.node().data);
  readonly typeLabelKey = computed(
    () => TYPE_LABEL_KEYS[this.data().type] ?? TYPE_LABEL_KEYS.department,
  );
  readonly cardClasses = computed(() => {
    const color = TYPE_CLASSES[this.data().type] ?? TYPE_CLASSES.department;
    // Ancestor context nodes are shown only for the path to root: greyed + inert.
    const context = this.data().contextOnly
      ? ' opacity-50 grayscale pointer-events-none'
      : '';
    return `${CARD_BASE} ${color}${context}`;
  });
  readonly subtitleClasses = computed(
    () => SUBTITLE_CLASSES[this.data().type] ?? SUBTITLE_CLASSES.department,
  );
  readonly section = computed(
    () => SECTION_CLASSES[this.data().type] ?? SECTION_CLASSES.department,
  );
  readonly toggleClasses = computed(
    () =>
      `flex h-7 w-full shrink-0 items-center gap-1.5 border-t px-3 text-[11px] font-medium transition-colors ${this.section().toggle}`,
  );
  readonly pillClasses = computed(
    () =>
      `inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold ${this.section().pill}`,
  );
  readonly positionCount = computed(() => this.data().positions?.length ?? 0);
  readonly positionsExpanded = computed(() =>
    this.store.expandedOeIdSet().has(this.data().oeId),
  );

  togglePositions(event: Event): void {
    event.stopPropagation();
    this.store.setOeExpanded(this.data().oeId, !this.positionsExpanded());
  }

  readonly stationCount = computed(() => this.data().stations?.length ?? 0);
  readonly stationsExpanded = computed(() =>
    this.store.expandedStationListOeIdSet().has(this.data().oeId),
  );

  toggleStations(event: Event): void {
    event.stopPropagation();
    this.store.setStationListExpanded(
      this.data().oeId,
      !this.stationsExpanded(),
    );
  }
  readonly canDelete = computed(() => {
    const data = this.data();
    return data.canDelete && !data.isDeletionLocked && !data.contextOnly;
  });
  /**
   * The card's one action.
   *
   * Editing the unit used to be a pencil beside it and is now the first entry
   * inside the panel this opens — the same right either way, which is why the
   * merge costs no permission: configuring an OE *is* an edit of that OE.
   */
  readonly canConfigure = computed(() => {
    const data = this.data();
    return data.canUpdate && !data.contextOnly;
  });

  onDelete(event: Event): void {
    if (!this.canDelete()) return;
    event.stopPropagation();
    this.store.remove({
      kind: 'orgUnit',
      id: this.data().oeId,
    });
  }

  onSettings(event: Event): void {
    if (!this.canConfigure()) return;
    event.stopPropagation();
    this.store.openSettings({
      kind: 'orgUnit',
      id: this.data().oeId,
    });
  }

  /** Whether the add menu would hold anything at all. */
  readonly canAddSomething = computed(() => {
    const data = this.data();
    return (
      data.canCreateChild &&
      (data.canCreateChildOe || data.canCreatePosition || data.canCreateStation)
    );
  });

  add(kind: 'orgUnit' | 'position' | 'station'): void {
    if (!this.data().canCreateChild) return;
    if (kind === 'position' && !this.data().canCreatePosition) return;
    if (kind === 'station' && !this.data().canCreateStation) return;
    if (kind === 'orgUnit') {
      if (!this.data().canCreateChildOe) return;
      this.store.create({
        kind: 'orgUnit',
        contextId: this.data().oeId,
      });
      return;
    }
    this.store.create({ kind, contextId: this.data().oeId });
  }
}
